'use client';

import { Suspense, startTransition, useState } from 'react';

import dynamic from 'next/dynamic';

import { SubscriptionLevelBadge } from '@ps/features/subscription/components/SubscriptionLevelBadge';
import { PopStoreExtendedSubscriptionLevel } from '@ps/features/subscription/types/SubscriptionLevel';
import { config } from '@ps/functions/config';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ps/ui/atoms/dropdown-menu';
import { cn } from '@ps/ui/utils';
import {
  MessageSquareMore,
  MoreVertical,
  UserCheck,
  UserX,
} from 'lucide-react';

import { useCommunityContext } from '../../contexts/community/useCommunityContext';
import type { ChannelMemberItem } from '../../functions/members/fetchChannelMembers';
import { useChannelMemberCounts } from '../../hooks/useChannelMemberCounts';
import { useChannelMembers } from '../../hooks/useChannelMembers';
import { useToggleMemberBlock } from '../../hooks/useToggleMemberBlock';
import type { CommunityChannel } from '../../types/CommunityChannel';
import { ChannelPanelSheet } from '../channel-info/ChannelPanelSheet';
import { BlockMemberDialog } from '../channel-message-view/BlockMemberDialog';
import { ChannelAvatar } from '../common/ChannelAvatar';

// The DM window drags in the whole messaging/websocket stack; it's owner-only
// and interaction-gated, so keep it behind a lazy boundary — loaded the first
// time the owner opens a DM (mirrors {@link MessageAuthorCard}).
const importChatWindow = () =>
  import('../channel-message-view/MemberChatWindow');
const MemberChatWindow = dynamic(() =>
  importChatWindow().then(mod => mod.MemberChatWindow),
);

interface Props {
  channel: CommunityChannel;
  open: boolean;
  onOpenChange: (next: boolean) => void;
}

/**
 * Members panel — list of channel members with role badge, a tab strip to
 * toggle the Blocked-only filter (with counts), and a per-member moderation
 * menu (Block/Unblock, DM Chat, Copy Reference). The moderation surface is
 * owner-only — the web port of PopshopRN's members page.
 */
export function ChannelMembersSheet({ channel, open, onOpenChange }: Props) {
  const [showBlocked, setShowBlocked] = useState(false);
  // "All" lists non-blocked members (`blocked: false`); "Blocked" lists the
  // blocked ones (`blocked: true`). The tab counts use the same filters so the
  // labels match their lists.
  const state = useChannelMembers(channel.id, { blocked: showBlocked });
  const counts = useChannelMemberCounts(channel.id);
  // The channel owner is its creator — used to badge the "Owner" row. This is
  // the STORE creator id (context), not the viewer: a member browsing the list
  // must see the creator badged, not themselves.
  const { creatorId, canManage } = useCommunityContext();

  const onModerated = () => {
    state.refresh();
    counts.refresh();
  };

  const withCount = (label: string, count: number | undefined) =>
    count == null ? label : `${label} (${count})`;

  return (
    <ChannelPanelSheet
      open={open}
      onOpenChange={onOpenChange}
      title="Channel Members"
      headerExtra={
        // The Blocked filter is a moderation view — only where management is
        // enabled (the dashboard), never on the visitor surface.
        canManage ? (
          <Tabs
            activeKey={showBlocked ? 'blocked' : 'all'}
            onChange={key => setShowBlocked(key === 'blocked')}
            tabs={[
              { key: 'all', label: withCount('All', counts.all) },
              { key: 'blocked', label: withCount('Blocked', counts.blocked) },
            ]}
          />
        ) : undefined
      }
    >
      {state.error ? (
        <p
          role="alert"
          className="bg-danger/10 rounded-md border border-red-500/30 px-3 py-2 text-xs text-red-200"
        >
          Couldn&rsquo;t load members. {state.error.message ?? ''}
        </p>
      ) : state.isLoading && state.members.length === 0 ? (
        <p className="text-content-4 text-xs">Loading…</p>
      ) : state.members.length === 0 ? (
        <p className="text-content-3 text-sm">
          {showBlocked
            ? 'No blocked members.'
            : 'No members yet — share the channel to invite people.'}
        </p>
      ) : (
        <ul className="divide-line divide-y">
          {state.members.map(member => (
            <MemberRow
              key={member.id}
              member={member}
              channel={channel}
              ownerUserId={creatorId ?? undefined}
              canManage={canManage}
              onModerated={onModerated}
            />
          ))}
        </ul>
      )}
    </ChannelPanelSheet>
  );
}

function MemberRow({
  member,
  channel,
  ownerUserId,
  canManage,
  onModerated,
}: {
  member: ChannelMemberItem;
  channel: CommunityChannel;
  ownerUserId: string | undefined;
  canManage: boolean;
  onModerated: () => void;
}) {
  const profile = member.profile;
  const displayName = formatName(profile);
  // The membership doc id IS the userId — matches PopshopRN's
  // `channelMemberSearchItemToMember.ts` adapter (uses `item.id` as
  // the member id and looks up profile by userId elsewhere).
  const isOwner = !!ownerUserId && member.id === ownerUserId;
  const level = member.subscriptionLevel;

  return (
    <li className="flex items-center gap-3 py-3">
      <ChannelAvatar src={profile.avatar} name={displayName} size={40} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className="text-content-1 truncate text-sm font-semibold">
            {displayName}
          </span>
          {profile.username ? (
            <span className="text-content-3 truncate text-xs">
              (@{profile.username})
            </span>
          ) : null}
        </div>
        <div className="mt-0.5">
          {isOwner ? (
            <span className="text-brand-text text-xs font-medium">Owner</span>
          ) : level === 'follower' ? (
            <span className="text-content-3 text-xs">Follower</span>
          ) : (
            <SubscriptionLevelBadge
              level={level as PopStoreExtendedSubscriptionLevel}
            />
          )}
        </div>
      </div>
      <MemberActionsMenu
        member={member}
        channel={channel}
        displayName={displayName}
        isSelf={isOwner}
        canManage={canManage}
        onModerated={onModerated}
      />
    </li>
  );
}

/**
 * Per-member moderation menu — Block/Unblock (owner, non-self), DM Chat (owner,
 * non-self), Copy Reference (dev/staging only). Renders nothing when no item
 * applies. 1:1 with PopshopRN's members-page `Actions`.
 */
function MemberActionsMenu({
  member,
  channel,
  displayName,
  isSelf,
  canManage,
  onModerated,
}: {
  member: ChannelMemberItem;
  channel: CommunityChannel;
  displayName: string;
  isSelf: boolean;
  canManage: boolean;
  onModerated: () => void;
}) {
  const { creatorId, bioContext } = useCommunityContext();
  const [blockOpen, setBlockOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  // Latches on the first "DM Chat" so the lazy chat chunk isn't fetched until
  // the owner actually opens a DM; once mounted Radix owns open/close.
  const [chatMounted, setChatMounted] = useState(false);
  const toggleBlock = useToggleMemberBlock({
    channelId: channel.id,
    memberId: member.id,
  });

  const canModerate = canManage && !isSelf;
  // Dev/staging only — the copied path is for internal tooling, matching
  // upstream's `!config.isProd || __DEV__` gate.
  const showCopyReference = config.env !== 'prod';

  if (!canModerate && !showCopyReference) return null;

  const copyReference = async () => {
    const ref = [
      'stores',
      creatorId,
      'communityChannels',
      channel.id,
      'members',
      member.id,
    ].join('/');
    try {
      await navigator.clipboard.writeText(ref);
    } catch (error: unknown) {
      // Clipboard can reject in insecure contexts — surface it rather than
      // swallow silently.
      console.error('Failed to copy member reference:', error);
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={`Actions for ${displayName}`}
            className="hover:text-content-1 data-[state=open]:text-content-1 text-content-2 shrink-0 transition"
          >
            <MoreVertical className="h-5 w-5" aria-hidden />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className="text-content-1 border-line bg-surface-1"
        >
          {canModerate ? (
            <DropdownMenuItem
              onSelect={() => setBlockOpen(true)}
              className={
                member.blocked ? undefined : 'text-red-400 focus:text-red-300'
              }
            >
              {member.blocked ? (
                <>
                  <UserCheck className="h-4 w-4" aria-hidden /> Unblock User
                </>
              ) : (
                <>
                  <UserX className="h-4 w-4" aria-hidden /> Block User
                </>
              )}
            </DropdownMenuItem>
          ) : null}
          {canModerate ? (
            <DropdownMenuItem
              onSelect={() => {
                // Mount the lazy chat window in a transition so React keeps the
                // panel visible while the chunk resolves.
                startTransition(() => {
                  setChatMounted(true);
                  setChatOpen(true);
                });
              }}
            >
              <MessageSquareMore className="h-4 w-4" aria-hidden /> DM Chat
            </DropdownMenuItem>
          ) : null}
          {canModerate && showCopyReference ? (
            <DropdownMenuSeparator className="bg-surface-2" />
          ) : null}
          {showCopyReference ? (
            <DropdownMenuItem onSelect={copyReference}>
              {/* Spacer matching the icon column so the text left-aligns with
                  the icon'd items above. */}
              <span className="h-4 w-4 shrink-0" aria-hidden />
              Copy Reference
            </DropdownMenuItem>
          ) : null}
        </DropdownMenuContent>
      </DropdownMenu>

      {canModerate ? (
        <BlockMemberDialog
          open={blockOpen}
          onOpenChange={setBlockOpen}
          blocked={member.blocked}
          memberName={displayName}
          onConfirm={async () => {
            await toggleBlock();
            onModerated();
          }}
        />
      ) : null}

      {chatMounted ? (
        // Local boundary: contain the lazy chunk's suspension so it never
        // bubbles to the panel / page Suspense boundary.
        <Suspense fallback={null}>
          <MemberChatWindow
            open={chatOpen}
            onOpenChange={setChatOpen}
            targetUserId={member.id}
            targetUser={{ name: displayName, avatar: member.profile.avatar }}
            bioContext={bioContext}
          />
        </Suspense>
      ) : null}
    </>
  );
}

interface TabsProps {
  activeKey: string;
  onChange: (key: string) => void;
  tabs: { key: string; label: string }[];
}

function Tabs({ activeKey, onChange, tabs }: TabsProps) {
  return (
    <div className="bg-surface-1 mb-2 flex rounded-full p-1">
      {tabs.map(tab => {
        const active = tab.key === activeKey;
        return (
          <button
            key={tab.key}
            type="button"
            onClick={() => onChange(tab.key)}
            className={cn(
              'flex-1 rounded-full px-3 py-1.5 text-xs font-medium transition',
              active
                ? 'text-content-1 bg-surface-3'
                : 'hover:text-content-1 text-content-3',
            )}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

function formatName(profile: {
  firstName: string;
  lastName: string;
  storeFrontName?: string;
}): string {
  if (profile.storeFrontName) return profile.storeFrontName;
  const first = profile.firstName?.trim() ?? '';
  const last = profile.lastName?.trim() ?? '';
  const composed = `${first} ${last}`.trim();
  return composed || 'Member';
}
